import { useRef, useState, useEffect } from 'react'
import classNames from 'classnames'
import Image from 'next/image'
import { useTranslation } from 'next-i18next'
import SvgArrowDownIcon from 'ui/svg/icons/stroke/arrow-down.svg'
import { AButton, ASvg, MImageWrapper, MVideoThumbnail } from 'ui'
import './c-hero.css'
import { CraftEmbeddedAssetField, CraftLinkItField } from 'tsconfig/craft-types'
import { useRouter } from 'next/router'
import TagManager from 'react-gtm-module'
import { useSiteInfo } from 'shared'
import { FormData, MFormSearch } from '../molecules/m-form-search'
import SvgSearchIcon from '../../svg/icons/stroke/search.svg'

export type CHeroBlock = {
  id?: string
  typeHandle?: string
  beforeHeading?: string
  heading?: string
  headingSecondary?: string
  displayHeadingOnTheSide: boolean
  featuredContent?: string
  displayContinueCta: boolean
  heroContent?: string
  borders?: string
  tooltipText?: string
  tooltipTextPlaceholder?: string
  image?: Array<{
    url: string
    title?: string
    height: number
    width: number
  }>
  video?: CraftEmbeddedAssetField[]
  cta?: CraftLinkItField
  ctaHeading?: CraftLinkItField
}

type Props = {
  field: Array<CHeroBlock>
  isProcedure?: boolean
}

const CHero = ({ field, isProcedure = false }: Props) => {
  const { t } = useTranslation('common')
  const {
    beforeHeading,
    heading,
    displayHeadingOnTheSide,
    heroContent,
    featuredContent,
    image,
    displayContinueCta,
    video,
    borders,
    cta,
    ctaHeading,
    tooltipText,
    tooltipTextPlaceholder,
  } = field?.[0] ?? []
  const router = useRouter()
  const isHomepage = Boolean(router.asPath === '/')
  const blockNormalRef = useRef<HTMLDivElement | null>(null)
  const buttonRef = useRef<HTMLDivElement | null>(null)
  const isLargeBorder = Boolean(borders === 'large')
  const isMp4 = Boolean(video?.[0] && !video?.[0]?.embeddedAsset)
  const siteInfo = useSiteInfo()
  const [isSticky, setIsSticky] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const mobileBreakpoint = 768

  const handleWindowResize = () => {
    setIsMobile(window.innerWidth < mobileBreakpoint)
  }

  const handleScrollButtonClick = () => {
    if (typeof window !== 'undefined') {
      const element = blockNormalRef.current
      // 106px  is the sum of the height between the header and the navigation
      window.scrollTo({
        top: element
          ? element.getBoundingClientRect().bottom + window.pageYOffset - 106
          : 0,
        behavior: 'smooth',
      })
    }
  }

  const heroFooter = field.find((item) => item.typeHandle === 'blockFooter')

  const submitDoctorSearch = (data: FormData) => {
    const piiRegex = /(\S+@\S+)/g

    if (piiRegex.test(data.q)) {
      console.log(
        'Refusing to search for something that looks like personal information.'
      )
      return
    }

    TagManager.dataLayer({
      dataLayer: {
        event: 'doctorSearch',
        event_category: 'Doctor Search',
        event_action: 'Find a Doctor component before footer',
        event_label: data.q,
      },
    })

    window.location.href = `${siteInfo.doctorUrl}?q=${encodeURIComponent(
      data.q
    )}`
  }

  const handleScroll = () => {
   handleWindowResize() 

    const button = buttonRef.current
    
    if (isMobile && button) {
      const stickyOffset = button.offsetTop
      const buffer = 200
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop

      if (scrollTop > stickyOffset + buffer) {
        setIsSticky(true)
      } else if (scrollTop < stickyOffset - buffer) {
        setIsSticky(false)
      }
    } else {
      setIsSticky(false)
    }
  }

  useEffect(() => {
    window.addEventListener('scroll', handleScroll)
    window.addEventListener('resize', handleScroll)

    return () => {
      window.removeEventListener('scroll', handleScroll)
      window.removeEventListener('resize', handleScroll)
    };
  }, [isMobile])

  useEffect(() => {
    window.addEventListener('resize', handleScroll)

    return () => {
      window.removeEventListener('resize', handleScroll)
    }
  }, [])

  return (
    <>
      <div
        ref={blockNormalRef}
        className={classNames('c-hero__wrapper', {
          'c-hero__bg-lg': isLargeBorder,
          'c-hero__bg-md': !isLargeBorder,
          'small-hero': isProcedure,
        })}
      >
        <div className="o-container o-container--xl">
          {beforeHeading && (
            <h2 className="uppercase tracking-wide md:text-center mb-[20px] md:mb-[40px]">
              {beforeHeading}
            </h2>
          )}
          {heading && (
            <h1
              className={classNames(
                'text-4xl leading-10 lg:text-heading text-center font-semibold drop-shadow-[0px_4px_4px_rgba(0,0,0,0.05)]',
                {
                  'lg:mb-[90px]': !ctaHeading?.url,
                  'mb-[40px]': ctaHeading?.url,
                  'md:block': !displayHeadingOnTheSide,
                  'md:hidden': displayHeadingOnTheSide,
                }
              )}
            >
              {heading}
            </h1>
          )}
           {ctaHeading?.url && (
            <div
              ref={buttonRef}
              className={classNames('justify-center md:flex hero-cta-heading', {
                sticky: isSticky,
              })}
            >
              <AButton
                className="w-full md:w-[303px] text-primary-dark-teal text-lg mb-[20px]"
                type={ctaHeading?.type === 'custom' || ctaHeading?.type === 
                  'entry' || ctaHeading?.type === 
                  'url' ? 'a' : ctaHeading?.type}
                href={ctaHeading?.url}
                rel={ctaHeading.type === 'url' ? 'noreferrer' : ''}
                target={ctaHeading.target}
                color="white"
                size="lg"
                style="solid"
                textTransform="none"
              >
                <span>{ctaHeading.text}</span>
              </AButton>
            </div>
          )}
          {cta?.url && (
            <div className={'flex justify-center md:hidden'}>
              <AButton
                className="w-full mt-[10px] mb-[15px] text-primary-dark-teal text-lg"
                type={
                  cta?.type === 'custom' ||
                  cta?.type === 'entry' ||
                  cta?.type === 'url'
                    ? 'a'
                    : cta?.type
                }
                href={cta?.url}
                rel={cta.type === 'url' ? 'noreferrer' : ''}
                target={cta.target}
                color="white"
                style="solid"
              >
                <span>{cta.text}</span>
              </AButton>
            </div>
          )}
          <div
            className={classNames('c-hero__columns', {
              'mt-[30px] md:mt-[90px]': heading && !displayHeadingOnTheSide,
            })}
          >
            <div
              className={classNames({
                'md:w-[45%]': displayHeadingOnTheSide && heading,
                'md:w-[40%]': heading && !displayHeadingOnTheSide,
              })}
            >
              {heading && (
                <h1
                  className={classNames(
                    'hidden text-3xl md:text-4xl lg:text-6xl font-semibold mb-[25px] drop-shadow-[0px_4px_4px_rgba(0,0,0,0.05)]',
                    {
                      'md:block': displayHeadingOnTheSide,
                    }
                  )}
                >
                  {heading}
                </h1>
              )}
              {heroContent && (
                <div
                  className={classNames(
                    'leading-6 md:leading-8 [&>p>a]:underline [&>p>a]:font-semibold',
                    {
                      'text-lg': displayHeadingOnTheSide,
                      'text-lg md:text-xl lg:text-2xl':
                        !displayHeadingOnTheSide,
                    }
                  )}
                  dangerouslySetInnerHTML={{ __html: heroContent }}
                />
              )}
              {featuredContent && (
                <div
                  className="c-hero__featured-content my-[20px]"
                  dangerouslySetInnerHTML={{ __html: featuredContent }}
                />
              )}
            </div>
            {Boolean(video?.length || image?.length) && (
              <MImageWrapper
                className={classNames('c-hero__image-wrapper', {
                  'flex-1': video?.[0]?.url,
                  'mx-auto': !video?.[0]?.url,
                })}
              >
                {isMp4 ? (
                  <video
                    className="w-full"
                    width="657"
                    height="452"
                    autoPlay
                    muted
                    playsInline
                    loop
                    aria-label={video?.[0].title ?? ''}
                  >
                    <source src={video?.[0]?.url} type="video/mp4" />
                  </video>
                ) : (
                  <>
                    {video?.[0]?.url ? (
                      <MVideoThumbnail video={video} />
                    ) : (
                      <Image
                        src={image?.[0].url ?? ''}
                        alt={image?.[0].title ?? ''}
                        width={image?.[0].width ?? 0}
                        height={image?.[0].height ?? 0}
                      />
                    )}
                  </>
                )}
              </MImageWrapper>
            )}
          </div>
          {isHomepage && tooltipText && (
            <div className="text-center mx-auto text-2xl mb-4 only-desktop">
              {tooltipText}
            </div>
          )}
          {cta?.url && (
            <div className={'justify-center hidden md:flex'}>
              <AButton
                className="w-full md:w-[420px] text-primary-dark-teal text-lg"
                type={
                  cta?.type === 'custom' ||
                  cta?.type === 'entry' ||
                  cta?.type === 'url'
                    ? 'a'
                    : cta?.type
                }
                href={cta?.url}
                rel={cta.type === 'url' ? 'noreferrer' : ''}
                target={cta.target}
                color="white"
                size="lg"
                style="solid"
              >
                <span>{cta.text}</span>
              </AButton>
            </div>
          )}
          {!cta?.url && tooltipText && (
            <div className="rounded-full md:bg-transparent text-primary-dark-teal md:top-[150px] max-w-[420px] mx-auto mt-2">
              <MFormSearch
                icon={
                  <ASvg
                    aria-label="Search"
                    className="stroke-current w-8 h-8"
                    svg={SvgSearchIcon}
                  />
                }
                text={tooltipTextPlaceholder || 'Enter Your Zip Code'}
                type="text"
                onSubmit={submitDoctorSearch}
                className="md:text-lg m-form-search__input text-button md:text-lg font-semibold border border-primary-dark-teal bg-neutral-white text-primary-dark-teal uppercase placeholder-primary-dark-teal"
              />
            </div>
          )}
        </div>
        {displayContinueCta && (
          <div className="c-hero__scroll-btn-wrapper">
            <button
              onClick={handleScrollButtonClick}
              className="c-hero__scroll-btn"
            >
              <ASvg
                className="stroke-current flex justify-center"
                svg={SvgArrowDownIcon}
                ariaLabel={t('arrow-down') ?? ''}
              />
            </button>
          </div>
        )}
      </div>
      {Boolean(heroFooter?.heading) && (
        <div className="c-hero-footer">
          <div className="o-container o-container--xl flex justify-around flex-col md:flex-row px-[20px]">
            {heroFooter?.heading && (
              <div
                className="c-hero-footer__heading"
                dangerouslySetInnerHTML={{ __html: heroFooter?.heading }}
              />
            )}
            {heroFooter?.headingSecondary && (
              <div
                className="c-hero-footer__heading"
                dangerouslySetInnerHTML={{
                  __html: heroFooter?.headingSecondary,
                }}
              />
            )}
          </div>
        </div>
      )}
    </>
  )
}

export { CHero }

export const CHeroFragment = `
    fragment hero on hero_MatrixField {
      ... on hero_block_BlockType {
        id
        typeHandle
        beforeHeading
        heading
        displayHeadingOnTheSide
        heroContent
        featuredContent
        tooltipText
        tooltipTextPlaceholder
        image {
          url @transform(mode: "crop", width: 800, immediately: true)
          title
          width
          height
          path
        }
        video {
          url
          title
          embeddedAsset {
            title
            url
            providerName
          }
        }
        borders
        displayContinueCta
        cta {
          type
          url
          text
          target
        }
        ctaHeading {
          type
          url
          text
          target
        }
      }
      ... on hero_blockFooter_BlockType {
        typeHandle
        heading
        headingSecondary
      }
    }
  `
